import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Column } from 'devextreme-react/data-grid';
import { Item as TabItem } from "devextreme-react/tab-panel";
import Form, { FormRef, GroupItem, SimpleItem, Label, RequiredRule } from "devextreme-react/form";
import TreeView, { TreeViewTypes } from 'devextreme-react/tree-view';
import { Button } from 'devextreme-react';
import ISchemaSite from "../../models/ISchemaSite";
import ISchema from "../../models/ISchema";
import ILookupItem from "../../models/ILookupItem";
import Guid from "../../models/Guid";
import FormSysteme from '../../components/forms/form-systeme';
import { FormListBase, FormListBaseTab, FormListBaseGrid, FormListBaseToolbar } from '../base';
import useCustomDataSource, { Mode } from "../../hooks/useCustomDataSource";
import { useContextPopupPage } from '../../components/popups/PopupPage';
import useLookUpBase from '../../components/selectbox/LookupBase';
import SchemaSitePlanCanvas, { IUniteSelectionPlan } from './schema-site-plan-canvas';
import './gestion-schemas.css';

interface IGestionSchemasProps {
  isModeSelection?: boolean,
}

const estLookupValide = (item: ILookupItem) =>
  !item.id.isEmpty() && item.description?.trim().toLowerCase() !== "aucun";

interface ISchemaTreeItem {
  id: string,
  text: string,
  hasItems?: boolean,
  items?: ISchemaTreeItem[],
  expanded?: boolean,
  itemType: 'typeUnite' | 'unite',
  idUnite?: Guid,
  disabled?: boolean,
}

export default function GestionSchemas({ isModeSelection }: IGestionSchemasProps) {
  const schemasFormDataSource = useCustomDataSource<ISchemaSite, ISchemaSite>({ path: "schemasite", primaryKey: "idSchemaSite", customSauvegarderPath: "schemasite/save" });
  const { setMainForm, selectedDataRow: selectedSchema, selectedId, mode, setMode, isFieldModifier, setIsFieldModifier, onFieldDataChanged, sauvegarder, dataSource } = schemasFormDataSource;
  const formRef = useRef<FormRef>(null);
  const { setSelectedValue } = useContextPopupPage();
  const { data: typesUnitesLookup } = useLookUpBase({
    controllerName: "typeunite",
    includeEmpty: false,
    showFind: false,
    showClear: false,
  });
  const { data: unitesLookup } = useLookUpBase({
    controllerName: "unite",
    includeEmpty: false,
    showFind: false,
    showClear: false,
  });
  const [planDetails, setPlanDetails] = useState<ISchema[]>([]);
  const planDetailsRef = useRef<ISchema[]>([]);
  const detailsInitiauxRef = useRef<ISchema[]>([]);
  const [selectedUnite, setSelectedUnite] = useState<IUniteSelectionPlan | null>(null);
  const [selectedPlanDetail, setSelectedPlanDetail] = useState<ISchema | null>(null);
  const [typesExpanses, setTypesExpanses] = useState<Set<string>>(new Set());
  const dernierIdChargeRef = useRef<string | undefined>(undefined);
  const sauvegardePlanRef = useRef(false);

  useEffect(() => {
    setMainForm(formRef.current?.instance());
  }, [setMainForm, selectedId, mode]);

  useEffect(() => {
    planDetailsRef.current = planDetails;
  }, [planDetails]);

  useEffect(() => {
    if (mode !== Mode.Lecture) formRef.current?.instance().getEditor("description")?.focus();
  }, [mode])

  const appliquerDetails = useCallback((details?: ISchema[]) => {
    const detailsCharges = details ?? [];
    detailsInitiauxRef.current = detailsCharges;
    setPlanDetails([...detailsCharges]);
    setSelectedUnite(null);
    setSelectedPlanDetail(null);
  }, []);

  const idSchemaSiteCourant = selectedId?.toString();

  useEffect(() => {
    if (mode === Mode.Ajout || sauvegardePlanRef.current) {
      return;
    }

    if (dernierIdChargeRef.current === idSchemaSiteCourant) {
      return;
    }

    dernierIdChargeRef.current = idSchemaSiteCourant;
    if (!selectedId || selectedId.toString() === Guid.empty.toString()) {
      appliquerDetails([]);
      return;
    }

    appliquerDetails(selectedSchema?.schemaSiteDetails);
  }, [appliquerDetails, idSchemaSiteCourant, mode, selectedId, selectedSchema?.schemaSiteDetails]);

  useEffect(() => {
    if (mode === Mode.Ajout) {
      detailsInitiauxRef.current = [];
      dernierIdChargeRef.current = undefined;
      setPlanDetails([]);
      setSelectedUnite(null);
      setSelectedPlanDetail(null);
      return;
    }

    if (mode !== Mode.Lecture || sauvegardePlanRef.current) {
      return;
    }

    setPlanDetails([...detailsInitiauxRef.current]);
    setSelectedUnite(null);
    setSelectedPlanDetail(null);
  }, [mode]);

  const descriptionOptions = useMemo(() => {
    return { maxLength: 100 };
  }, []);
  const boutonEditorOptions = useMemo(() => {
    return {
      min: 0,
      showSpinButtons: true,
      width: '100%',
    };
  }, []);

  const typesUnites = useMemo(
    () => (typesUnitesLookup ?? []).filter(estLookupValide),
    [typesUnitesLookup]
  );

  const idsUnitesPlacees = useMemo(
    () => new Set(planDetails.map((detail) => detail.idUnite?.toString())),
    [planDetails]
  );

  const unitesParType = useMemo(() => {
    const map: Record<string, ISchemaTreeItem[]> = {};

    (unitesLookup ?? [])
      .filter((unite) => unite.actif && estLookupValide(unite) && !unite.idParent.isEmpty())
      .forEach((unite) => {
        const typeId = unite.idParent.toString();
        if (!map[typeId]) {
          map[typeId] = [];
        }

        map[typeId].push({
          id: `${typeId}-${unite.id.toString()}`,
          text: unite.description,
          itemType: 'unite',
          idUnite: unite.id,
          disabled: idsUnitesPlacees.has(unite.id.toString()),
        });
      });

    return map;
  }, [idsUnitesPlacees, unitesLookup]);

  const treeItems = useMemo((): ISchemaTreeItem[] => (
    typesUnites.map((item) => {
      const typeId = item.id.toString();
      const unites = unitesParType[typeId] ?? [];

      return {
        id: typeId,
        text: item.description,
        hasItems: unites.length > 0,
        items: unites,
        expanded: typesExpanses.has(typeId),
        itemType: 'typeUnite',
      };
    })
  ), [typesExpanses, typesUnites, unitesParType]);

  const marquerPlanModifie = useCallback(() => {
    if (mode === Mode.Lecture) {
      return;
    }
    setIsFieldModifier(true);
  }, [mode, setIsFieldModifier]);

  const onTreeItemClick = useCallback((e: TreeViewTypes.ItemClickEvent) => {
    const item = e.itemData as ISchemaTreeItem | undefined;
    if (!item || item.itemType !== 'unite' || !item.idUnite || item.disabled) {
      setSelectedUnite(null);
      return;
    }

    setSelectedUnite({
      idUnite: item.idUnite,
      description: item.text,
    });
  }, []);

  const onTreeItemDragStart = useCallback((item: ISchemaTreeItem, event: React.DragEvent<HTMLElement> | DragEvent) => {
    if (mode === Mode.Lecture || item.itemType !== 'unite' || item.disabled || !item.idUnite) {
      event.preventDefault();
      return;
    }

    const payload = JSON.stringify({
      type: 'unite',
      idUnite: item.idUnite.toString(),
      description: item.text,
    });
    event.dataTransfer?.setData('text/plain', payload);
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'copy';
    }

    setSelectedUnite({
      idUnite: item.idUnite,
      description: item.text,
    });
  }, [mode]);

  const renderTreeItem = useCallback((item: ISchemaTreeItem) => {
    const peutGlisser = mode !== Mode.Lecture && item.itemType === 'unite' && !item.disabled;
    return (
      <div
        className={`gestion-schemas-site-form-arbre-item${item.itemType === 'unite' ? ' gestion-schemas-site-form-arbre-item-unite' : ''}${peutGlisser ? ' gestion-schemas-site-form-arbre-item-unite--glisser' : ''}`}
        draggable={peutGlisser}
        onDragStart={(event) => onTreeItemDragStart(item, event)}
      >
        {item.text}
      </div>
    );
  }, [mode, onTreeItemDragStart]);

  const onTreeItemExpanded = useCallback((e: TreeViewTypes.ItemExpandedEvent) => {
    const item = e.itemData as ISchemaTreeItem | undefined;
    if (item?.itemType !== 'typeUnite') {
      return;
    }

    setTypesExpanses((prev) => {
      if (prev.has(item.id)) {
        return prev;
      }
      const next = new Set(prev);
      next.add(item.id);
      return next;
    });
  }, []);

  const onTreeItemCollapsed = useCallback((e: TreeViewTypes.ItemCollapsedEvent) => {
    const item = e.itemData as ISchemaTreeItem | undefined;
    if (item?.itemType !== 'typeUnite') {
      return;
    }

    setTypesExpanses((prev) => {
      if (!prev.has(item.id)) {
        return prev;
      }
      const next = new Set(prev);
      next.delete(item.id);
      return next;
    });
  }, []);

  const onPlaceUnite = useCallback((draft: IUniteSelectionPlan & { ligne: number, colonne: number }) => {
    if (mode === Mode.Lecture) {
      return;
    }

    const detailsActuels = planDetailsRef.current;
    const dejaPlacee = detailsActuels.some((detail) => detail.idUnite.equals(draft.idUnite));
    const celluleOccupee = detailsActuels.some((detail) => detail.ligne === draft.ligne && detail.colonne === draft.colonne);
    if (dejaPlacee || celluleOccupee) {
      return;
    }

    const nouveauDetail: ISchema = {
      idSchemaSiteDetail: Guid.newTemporary(),
      idSchemaSite: selectedSchema?.idSchemaSite || Guid.empty,
      idUnite: draft.idUnite,
      description: draft.description,
      ligne: draft.ligne,
      colonne: draft.colonne,
    };

    setPlanDetails((prev) => [...prev, nouveauDetail]);
    setSelectedPlanDetail(nouveauDetail);
    setSelectedUnite(null);
    marquerPlanModifie();
  }, [marquerPlanModifie, mode, selectedSchema?.idSchemaSite]);

  const onMoveUnite = useCallback((detail: ISchema, cellule: { ligne: number, colonne: number }) => {
    if (mode === Mode.Lecture) {
      return;
    }

    if (detail.ligne === cellule.ligne && detail.colonne === cellule.colonne) {
      setSelectedPlanDetail(detail);
      return;
    }

    const celluleOccupee = planDetailsRef.current.some((existant) =>
      existant.ligne === cellule.ligne
      && existant.colonne === cellule.colonne
      && !existant.idSchemaSiteDetail?.equals(detail.idSchemaSiteDetail)
    );
    if (celluleOccupee) {
      return;
    }

    const detailDeplace: ISchema = {
      ...detail,
      ligne: cellule.ligne,
      colonne: cellule.colonne,
    };

    setPlanDetails((prev) => prev.map((existant) =>
      existant.idSchemaSiteDetail?.equals(detail.idSchemaSiteDetail) ? detailDeplace : existant
    ));
    setSelectedPlanDetail(detailDeplace);
    setSelectedUnite(null);
    marquerPlanModifie();
  }, [marquerPlanModifie, mode]);

  const onSelectPlanDetail = useCallback((detail: ISchema | null) => {
    setSelectedPlanDetail(detail);
    if (detail) {
      setSelectedUnite(null);
    }
  }, []);

  const onEffacerBouton = useCallback((detail?: ISchema) => {
    const detailAEffacer = detail || selectedPlanDetail;
    if (mode === Mode.Lecture || !detailAEffacer) {
      return;
    }

    setPlanDetails((prev) => prev.filter((item) => !item.idSchemaSiteDetail?.equals(detailAEffacer.idSchemaSiteDetail)));
    setSelectedPlanDetail(null);
    marquerPlanModifie();
  }, [marquerPlanModifie, mode, selectedPlanDetail]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Delete') {
        return;
      }

      const cible = event.target as HTMLElement | null;
      const tag = cible?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || cible?.isContentEditable) {
        return;
      }

      if (mode === Mode.Lecture || !selectedPlanDetail) {
        return;
      }

      event.preventDefault();
      onEffacerBouton(selectedPlanDetail);
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [mode, onEffacerBouton, selectedPlanDetail]);

  const onSauvegarderClick = useCallback(async (e: { preventDefault?: boolean }) => {
    if (!sauvegarder) {
      return;
    }

    e.preventDefault = true;
    const validation = formRef.current?.instance().validate();
    if (validation && !validation.isValid) {
      return;
    }

    if (selectedSchema) {
      selectedSchema.schemaSiteDetails = planDetailsRef.current;
    }

    sauvegardePlanRef.current = true;
    const result = await sauvegarder(e);
    if (!result) {
      sauvegardePlanRef.current = false;
      return;
    }

    const schema = await dataSource?.store().byKey(result) as ISchemaSite | undefined;
    appliquerDetails(schema?.schemaSiteDetails);
    dernierIdChargeRef.current = schema?.idSchemaSite?.toString() ?? idSchemaSiteCourant;
    sauvegardePlanRef.current = false;
  }, [appliquerDetails, dataSource, idSchemaSiteCourant, sauvegarder, selectedSchema]);

  const renderArbreUnites = useCallback(() => (
    <div className={"gestion-schemas-site-form-arbre-unites"}>
      <TreeView
        items={treeItems}
        dataStructure={"tree"}
        displayExpr={"text"}
        keyExpr={"id"}
        height={"100%"}
        width={"100%"}
        expandEvent={"click"}
        selectByClick={true}
        selectionMode={"single"}
        focusStateEnabled={true}
        onItemClick={onTreeItemClick}
        onItemExpanded={onTreeItemExpanded}
        onItemCollapsed={onTreeItemCollapsed}
        itemRender={renderTreeItem}
      />
    </div>
  ), [onTreeItemClick, onTreeItemCollapsed, onTreeItemExpanded, renderTreeItem, treeItems]);

  const renderOptionSelection = useCallback(() => (
    <div>
      <div className={"gestion-schemas-site-form-option-selection-message"}>
        {selectedPlanDetail ? selectedPlanDetail.description : "Aucune sélection active"}
      </div>
      <Button
        text={"Effacer Bouton"}
        disabled={mode === Mode.Lecture || !selectedPlanDetail}
        onClick={() => onEffacerBouton()}
      />
    </div>
  ), [mode, onEffacerBouton, selectedPlanDetail]);

  const renderPlanCanvas = useCallback(() => (
    <SchemaSitePlanCanvas
      details={planDetails}
      largeurBouton={selectedSchema?.largeurBouton || 0}
      hauteurBouton={selectedSchema?.hauteurBouton || 0}
      espacementBouton={selectedSchema?.espacementBouton || 0}
      selectedUnite={selectedUnite}
      selectedDetail={selectedPlanDetail}
      readOnly={mode === Mode.Lecture}
      onPlace={onPlaceUnite}
      onMove={onMoveUnite}
      onSelectDetail={onSelectPlanDetail}
    />
  ), [mode, onMoveUnite, onPlaceUnite, onSelectPlanDetail, planDetails, selectedPlanDetail, selectedSchema?.espacementBouton, selectedSchema?.hauteurBouton, selectedSchema?.largeurBouton, selectedUnite]);

  return (
    <FormListBase key={"frm-schemasite"} data={schemasFormDataSource}>
      <FormListBaseGrid
        dataSource={schemasFormDataSource.dataSource}
        onFocusedRowChanged={(e: any) => {
          if (mode === Mode.Modifier) {
            setMode(Mode.Lecture);
          }
          if (setSelectedValue && e.row?.data)
            setSelectedValue(e.row.data);
        }}>
        <Column key={"grid-col-description"} dataField={"description"} caption="Description" allowHiding={false} />
      </FormListBaseGrid>
      <FormListBaseToolbar
        headerText={`${selectedSchema?.description || ''}`}
        sauvegarderOptions={{ disabled: !isFieldModifier, click: onSauvegarderClick }}
        annulerOptions={{ disabled: !isFieldModifier }}
        effacerOptions={{ disabled: !isFieldModifier }}
      />
      {!isModeSelection || isModeSelection && mode !== Mode.Lecture ? <FormListBaseTab>
        <TabItem title="Informations">
          <Form ref={formRef} readOnly={mode === Mode.Lecture} alignItemLabelsInAllGroups={true} formData={selectedSchema || {} as ISchemaSite} className={"forms gestion-schemas-site-form"} labelLocation={"left"} showColonAfterLabel={true} showRequiredMark={false} onFieldDataChanged={onFieldDataChanged}>
            <GroupItem cssClass={"gestion-schemas-site-form-entete"}>
              <GroupItem colCount={4} cssClass={"gestion-schemas-site-form-entete-dimensions-boutons"}>
              <SimpleItem key={"frm-description"} dataField={"description"} editorOptions={descriptionOptions} isRequired={true}>
                <Label text={"Description"} />
                <RequiredRule message={"La description est requise."} />
              </SimpleItem>
                <SimpleItem key={"frm-largeur-bouton"} dataField={"largeurBouton"} editorType={"dxNumberBox"} editorOptions={boutonEditorOptions}>
                  <Label text={"Largeur boutons"} />
                </SimpleItem>
                <SimpleItem key={"frm-hauteur-bouton"} dataField={"hauteurBouton"} editorType={"dxNumberBox"} editorOptions={boutonEditorOptions}>
                  <Label text={"Hauteur boutons"} />
                </SimpleItem>
                <SimpleItem key={"frm-espacement-bouton"} dataField={"espacementBouton"} editorType={"dxNumberBox"} editorOptions={boutonEditorOptions}>
                  <Label text={"Espacement boutons"} />
                </SimpleItem>
              </GroupItem>
            </GroupItem>
            <GroupItem colCount={4} cssClass={"gestion-schemas-site-form-zone-travail"}>
              <GroupItem colSpan={1} caption={"Sélection des unités"} cssClass={"gestion-schemas-site-form-selection-unites"}>
                <SimpleItem key={"frm-arbre-unites"} render={renderArbreUnites} />
                <GroupItem caption={"Option de la sélection"} cssClass={"gestion-schemas-site-form-option-selection"}>
                  <SimpleItem key={"frm-option-selection"} render={renderOptionSelection} />
                </GroupItem>
              </GroupItem>
              <GroupItem colSpan={3} caption={"Plan"} cssClass={"gestion-schemas-site-form-plan"}>
                <SimpleItem key={"frm-plan"} render={renderPlanCanvas} />
              </GroupItem>
            </GroupItem>
          </Form>
        </TabItem>
        <TabItem title="Système"><FormSysteme formData={selectedSchema || {} as ISchemaSite} /></TabItem>
      </FormListBaseTab> : null}
    </FormListBase>
  );
}
